import type { MouseEvent, ReactNode } from 'react';
import {
  copyMessengerImageToClipboard,
  saveMessengerAttachment,
} from '@/lib/messengerAttachmentClipboard';
import type { MessengerFileAttachmentTarget } from '@/components/Messenger/shared/messages/messengerAttachmentTarget';

const copyIcon = (
  <i className="fa-regular fa-copy" style={{ fontSize: 18, width: 18, textAlign: 'center' }} aria-hidden />
);

const downloadIcon = (
  <i className="fa-solid fa-download" style={{ fontSize: 18, width: 18, textAlign: 'center' }} aria-hidden />
);

const imageIcon = (
  <i className="fa-regular fa-image" style={{ fontSize: 18, width: 18, textAlign: 'center' }} aria-hidden />
);

type AttachmentMenuToast = {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
};

function preventMouseFocusLoss(e: MouseEvent<HTMLButtonElement>): void {
  e.preventDefault();
}

function menuItem(
  key: string,
  label: string,
  icon: ReactNode,
  onActivate: () => void,
): { key: string; node: ReactNode } {
  return {
    key,
    node: (
      <li key={key} className="context-menu__list-item">
        <button
          type="button"
          className="context-menu__item"
          onMouseDown={preventMouseFocusLoss}
          onClick={onActivate}
          role="menuitem"
        >
          <span className="context-menu__iconCell" aria-hidden>
            {icon}
          </span>
          <span className="context-menu__item-text">{label}</span>
        </button>
      </li>
    ),
  };
}

/** Пункты меню для встроенного изображения и файлового вложения. */
export function buildMessageAttachmentContextMenuItems(
  target: MessengerFileAttachmentTarget | null,
  toast: AttachmentMenuToast,
  onClose: () => void,
): { key: string; node: ReactNode }[] {
  if (!target) return [];

  const items: { key: string; node: ReactNode }[] = [];

  if (target.kind === 'image') {
    items.push(
      menuItem('copy-image', 'Копировать изображение', imageIcon, () => {
        void (async () => {
          const ok = await copyMessengerImageToClipboard(target.url);
          if (ok) toast.showSuccess('Изображение скопировано');
          else toast.showError('Не удалось скопировать изображение');
          onClose();
        })();
      }),
      menuItem('save-image', 'Сохранить изображение', downloadIcon, () => {
        void (async () => {
          const ok = await saveMessengerAttachment(target.url, target.fileName);
          if (ok) toast.showSuccess('Изображение сохранено');
          else toast.showError('Не удалось сохранить изображение');
          onClose();
        })();
      }),
    );
  } else {
    items.push(
      menuItem('save-file', 'Сохранить вложение', downloadIcon, () => {
        void (async () => {
          const ok = await saveMessengerAttachment(target.url, target.fileName);
          if (ok) toast.showSuccess('Вложение сохранено');
          else toast.showError('Не удалось сохранить вложение');
          onClose();
        })();
      }),
    );
  }

  return items;
}

/** Пункты для ContextMenu `items` API (Agent rows). */
export function buildAgentAttachmentContextMenuItems(
  target: MessengerFileAttachmentTarget | null,
  toast: AttachmentMenuToast,
  onClose: () => void,
): Array<{
  label: string;
  icon: ReactNode;
  onClick: () => void;
  onMouseDown: (e: MouseEvent<HTMLButtonElement>) => void;
}> {
  if (!target) return [];

  const withMouseGuard = (activate: () => void) => ({
    onMouseDown: preventMouseFocusLoss,
    onClick: activate,
  });

  const out: Array<{
    label: string;
    icon: ReactNode;
    onClick: () => void;
    onMouseDown: (e: MouseEvent<HTMLButtonElement>) => void;
  }> = [];

  if (target.kind === 'image') {
    out.push({
      label: 'Копировать изображение',
      icon: imageIcon,
      ...withMouseGuard(() => {
        void (async () => {
          const ok = await copyMessengerImageToClipboard(target.url);
          if (ok) toast.showSuccess('Изображение скопировано');
          else toast.showError('Не удалось скопировать изображение');
          onClose();
        })();
      }),
    });
    out.push({
      label: 'Сохранить изображение',
      icon: downloadIcon,
      ...withMouseGuard(() => {
        void (async () => {
          const ok = await saveMessengerAttachment(target.url, target.fileName);
          if (ok) toast.showSuccess('Изображение сохранено');
          else toast.showError('Не удалось сохранить изображение');
          onClose();
        })();
      }),
    });
  } else {
    out.push({
      label: 'Сохранить вложение',
      icon: downloadIcon,
      ...withMouseGuard(() => {
        void (async () => {
          const ok = await saveMessengerAttachment(target.url, target.fileName);
          if (ok) toast.showSuccess('Вложение сохранено');
          else toast.showError('Не удалось сохранить вложение');
          onClose();
        })();
      }),
    });
  }

  return out;
}

/** Иконка пункта копирования текста (единый стиль). */
export function messageContextCopyTextIcon(): ReactNode {
  return copyIcon;
}
